import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect } from "react";
import { Route, Switch, useLocation, Redirect } from "wouter";
import { trackOpenAIPageView } from "@/lib/analytics";
import { sayfaGir } from "@/lib/talep_kaynagi";
import { sayfaGoruntulendi } from "@/lib/olcum";
import CerezBanner from "@/components/CerezBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { RoasProvider } from "./contexts/RoasContext";
import ScrollToTop from "./components/ScrollToTop";
import { TUM_YONLENDIRMELER } from "@/lib/yonlendirmeler";

// Ana sayfa ilk boyamada hazir olsun diye statik yuklenir;
// diger tum sayfalar route bazli kod bolme (code splitting) ile
// yalnizca ziyaret edildiklerinde indirilir (Core Web Vitals).
import Home from "./pages/Home";

const NotFound = lazy(() => import("@/pages/NotFound"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Developments = lazy(() => import("./pages/Developments"));
const References = lazy(() => import("./pages/References"));
const Prices = lazy(() => import("./pages/Prices"));
const SSS = lazy(() => import("./pages/SSS"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const FacebookKiralama = lazy(() => import("./pages/FacebookKiralama"));

// Hizmet Alt Sayfaları
const ETicaret = lazy(() => import("./pages/services/ETicaret"));
const EIhracat = lazy(() => import("./pages/services/EIhracat"));
const ReklamYonetimi = lazy(() => import("./pages/services/ReklamYonetimi"));
const UgcInfluencer = lazy(() => import("./pages/services/UgcInfluencer"));
const SeoGeo = lazy(() => import("./pages/services/SeoGeo"));
const PazarYeri = lazy(() => import("./pages/services/PazarYeri"));
const Kreatif = lazy(() => import("./pages/services/Kreatif"));
const Otomasyon = lazy(() => import("./pages/services/Otomasyon"));
const FileManager = lazy(() => import("./pages/FileManager"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const ROICalculator = lazy(() => import("./pages/ROICalculator"));
const PixelKayipHesaplayici = lazy(() => import("./pages/araclar/PixelKayipHesaplayici"));
const TesetturSezonTakvimi = lazy(() => import("./pages/araclar/TesetturSezonTakvimi"));
const MetaHesapDenetimi = lazy(() => import("./pages/araclar/MetaHesapDenetimi"));
const YazarSayfasi = lazy(() => import("./pages/YazarSayfasi"));
const TemaMadrid = lazy(() => import("./pages/TemaMadrid"));
const TemaKilavuz = lazy(() => import("./pages/TemaKilavuz"));
const Glossary = lazy(() => import("./pages/Glossary"));
const GlossaryDetail = lazy(() => import("./pages/GlossaryDetail"));
const Appointment = lazy(() => import("./pages/Appointment"));
const SiteHaritasi = lazy(() => import("./pages/SiteHaritasi"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));

// SEO/GEO Açılış Sayfaları
const IstanbulEticaretReklamAjansi = lazy(() => import("./pages/landing/IstanbulEticaretReklamAjansi"));
const SehirEticaretReklamAjansi = lazy(() => import("./pages/landing/SehirEticaretReklamAjansi"));
// Ajans odaklı kelime kümeleri (tek şablon, veri: lib/ajans_landing_data.ts)
const AjansLanding = lazy(() => import("./pages/landing/AjansLanding"));
// Shopify / ikas hizmet menüsü sayfaları (veri: lib/platform_hizmet_data.ts)
const PlatformHub = lazy(() => import("./pages/platform/PlatformHub"));
const PlatformHizmet = lazy(() => import("./pages/platform/PlatformHizmet"));

// Sektörel Çözümler (B2B: Moda & Tesettür)
const KadinGiyim = lazy(() => import("./pages/sektorel/KadinGiyim"));
const TesetturGiyim = lazy(() => import("./pages/sektorel/TesetturGiyim"));
const ButikInstagram = lazy(() => import("./pages/sektorel/ButikInstagram"));
const SektorDinamik = lazy(() => import("./pages/sektorel/SektorDinamik"));

// Sayfa gecislerinde kisa yukleme gostergesi (marka stilinde)
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <span className="bg-black text-[#9EFF00] font-bold text-xs tracking-widest px-3 py-1.5 border-2 border-black shadow-[3px_3px_0px_0px_rgba(158,255,0,1)]">
          DİJİPO
        </span>
        <div className="w-32 h-1 bg-neutral-200 overflow-hidden">
          <div className="h-full w-1/3 bg-[#9EFF00] border-r border-black animate-[djslide_1.1s_ease-in-out_infinite]" />
        </div>
      </div>
    </div>
  );
}

// Yasal Sayfalar (tembel yukleme — ana pakete girmez)
const KVKKAydinlatma = lazy(() => import("./pages/legal/KVKKAydinlatma"));
const GizlilikPolitikasi = lazy(() => import("./pages/legal/GizlilikPolitikasi"));
const CerezPolitikasi = lazy(() => import("./pages/legal/CerezPolitikasi"));
const KullanimKosullari = lazy(() => import("./pages/legal/KullanimKosullari"));
const HizmetSozlesmesi = lazy(() => import("./pages/legal/HizmetSozlesmesi"));
const IptalIade = lazy(() => import("./pages/legal/IptalIade"));
function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Switch>
      {/* 301 ile birleştirilen sayfalar ve kısa eş adresler (/faq → /sss): istemci tarafı yönlendirme (sunucu 301 döner) */}
      {Object.entries(TUM_YONLENDIRMELER).map(([eski, yeni]) => (
        <Route key={eski} path={eski}>{() => <Redirect to={yeni} replace />}</Route>
      ))}
      <Route path="/" component={Home} />
      <Route path="/kurumsal" component={About} />
      <Route path="/hizmetlerimiz" component={Services} />
      <Route path="/facebook-reklam-hesabi-kiralama" component={FacebookKiralama} />
      <Route path="/gelistirmelerimiz" component={Developments} />
      <Route path="/referanslar" component={References} />
      <Route path="/referanslar/:id" component={CaseStudyDetail} />
      <Route path="/roi-hesaplayici" component={ROICalculator} />
      <Route path="/pixel-kayip-hesaplayici" component={PixelKayipHesaplayici} />
      <Route path="/tesettur-sezon-takvimi" component={TesetturSezonTakvimi} />
      <Route path="/ucretsiz-meta-hesap-denetimi" component={MetaHesapDenetimi} />
      <Route path="/yazar/:slug" component={YazarSayfasi} />
      <Route path="/dijipo-x-madrid-tema" component={TemaMadrid} />
      <Route path="/dijipo-x-madrid-tema/kullanim-kilavuzu" component={TemaKilavuz} />
      <Route path="/sozluk" component={Glossary} />
      <Route path="/sozluk/:slug" component={GlossaryDetail} />
      <Route path="/randevu" component={Appointment} />
      <Route path="/site-haritasi" component={SiteHaritasi} />
      <Route path="/admin" component={AdminPanel} />
      <Route path="/fiyatlar" component={Prices} />
      <Route path="/sss" component={SSS} />
      <Route path="/blog" component={Blog} />
      <Route path="/blog/:id" component={BlogDetail} />

      {/* Hizmet Alt Sayfaları Rotaları */}
      <Route path="/hizmetlerimiz/e-ticaret" component={ETicaret} />
      <Route path="/hizmetlerimiz/e-ihracat" component={EIhracat} />
      <Route path="/hizmetlerimiz/reklam-yonetimi" component={ReklamYonetimi} />
      <Route path="/hizmetlerimiz/ugc-influencer" component={UgcInfluencer} />
      <Route path="/hizmetlerimiz/seo-geo" component={SeoGeo} />
      <Route path="/hizmetlerimiz/pazar-yeri" component={PazarYeri} />
      <Route path="/hizmetlerimiz/kreatif-tasarim" component={Kreatif} />
      <Route path="/hizmetlerimiz/pazarlama-otomasyonu" component={Otomasyon} />

      {/* SEO/GEO Açılış Sayfaları */}
      <Route path="/istanbul-eticaret-reklam-ajansi" component={IstanbulEticaretReklamAjansi} />
      <Route path="/izmir-eticaret-reklam-ajansi" component={SehirEticaretReklamAjansi} />
      <Route path="/ankara-eticaret-reklam-ajansi" component={SehirEticaretReklamAjansi} />
      <Route path="/antalya-eticaret-reklam-ajansi" component={SehirEticaretReklamAjansi} />
      <Route path="/bursa-eticaret-reklam-ajansi" component={SehirEticaretReklamAjansi} />
      <Route path="/e-ticaret-reklam-ajansi" component={AjansLanding} />
      <Route path="/google-ads-ajansi" component={AjansLanding} />
      <Route path="/meta-reklam-ajansi" component={AjansLanding} />
      <Route path="/performans-pazarlama-ajansi" component={AjansLanding} />
      <Route path="/e-ticaret-danismanligi" component={AjansLanding} />
      <Route path="/ikas-reklam-ajansi" component={AjansLanding} />
      <Route path="/ikas-danismanligi" component={AjansLanding} />
      <Route path="/ikastan-shopifya-gecis" component={AjansLanding} />
      <Route path="/shopify-reklam-ajansi" component={AjansLanding} />
      <Route path="/shopify" component={PlatformHub} />
      <Route path="/ikas" component={PlatformHub} />
      <Route path="/shopify/:slug" component={PlatformHizmet} />
      <Route path="/ikas/:slug" component={PlatformHizmet} />

      {/* Sektörel Çözümler */}
      <Route path="/sektorel/kadin-giyim-reklam-ajansi" component={KadinGiyim} />
      <Route path="/sektorel/tesettur-giyim-dijital-pazarlama" component={TesetturGiyim} />
      <Route path="/sektorel/butikler-icin-instagram-reklamlari" component={ButikInstagram} />
      {/* Veri tabanlı sektör sayfaları (kozmetik, takı, ayakkabı-çanta, dekorasyon, Google Ads) */}
      <Route path="/sektorel/:slug" component={SektorDinamik} />

      <Route path="/dosyalar" component={FileManager} />

      {/* Yasal Sayfalar */}
      <Route path="/kvkk-aydinlatma-metni" component={KVKKAydinlatma} />
      <Route path="/gizlilik-politikasi" component={GizlilikPolitikasi} />
      <Route path="/cerez-politikasi" component={CerezPolitikasi} />
      <Route path="/kullanim-kosullari" component={KullanimKosullari} />
      <Route path="/hizmet-sozlesmesi" component={HizmetSozlesmesi} />
      <Route path="/iptal-ve-iade-politikasi" component={IptalIade} />

      {/* Veri tabanlı açılış sayfaları (ajans_landing_data: AI reklam, altyapı/geçiş vb.) — tek segmentli yollar */}
      <Route path="/:slug" component={AjansLanding} />

      <Route path="/404" component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

// ChatGPT (OpenAI) page_viewed dönüşümü + talep kaynağı sayfa izi + çerezsiz toplu ziyaret sayımı — ilk yükleme + her rota değişiminde
function OpenAIPageTracker() {
  const [location] = useLocation();
  useEffect(() => {
    trackOpenAIPageView();
    sayfaGir(location);
    sayfaGoruntulendi(location);
  }, [location]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <RoasProvider>
          <TooltipProvider>
            <Toaster />
            <ScrollToTop />
            <OpenAIPageTracker />
            <Suspense fallback={<PageLoader />}>
              <Router />
            </Suspense>
            <CerezBanner />
          </TooltipProvider>
        </RoasProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
